/** Typed getters for /api/admin/* (first-party, same-origin via the Pages Function). */
import type { PeriodKey, VatMode } from "@apphq/shared/metrics";
import type {
  AppMetricsResponse,
  CustomerDetailResponse,
  CustomerListResponse,
  DeviceActivityResponse,
  UsageResponse,
  Environment,
  OverviewResponse,
  SettingsResponse,
} from "@apphq/shared/types";
import { apiGet, apiPost } from "../api";
import { buildQuery } from "./query";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function readError(res: Response): Promise<string> {
  try {
    const j = (await res.json()) as { error?: string };
    return j.error ?? `Fout ${res.status}`;
  } catch {
    return `Fout ${res.status}`;
  }
}

async function parse<T>(res: Response): Promise<T> {
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  return (await res.json()) as T;
}

export async function adminGet<T>(path: string): Promise<T> {
  return parse<T>(await apiGet(path));
}

export async function adminSend<T>(method: "POST" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T> {
  if (method === "POST") return parse<T>(await apiPost(path, body));
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body !== undefined ? { "content-type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return parse<T>(res);
}

export interface Scope {
  period: PeriodKey;
  environment: Environment;
  vat: VatMode;
}

export const getOverview = (s: Scope) => adminGet<OverviewResponse>(`/api/admin/overview${buildQuery({ ...s })}`);
export const getAppMetrics = (id: string, s: Scope) =>
  adminGet<AppMetricsResponse>(`/api/admin/apps/${encodeURIComponent(id)}/metrics${buildQuery({ ...s })}`);
export const getCustomers = (q: { q?: string; app_id?: string; environment: Environment }) =>
  adminGet<CustomerListResponse>(`/api/admin/customers${buildQuery(q)}`);
export const getCustomer = (appUserId: string, q: { app_id: string; environment: Environment; vat: VatMode }) =>
  adminGet<CustomerDetailResponse>(`/api/admin/customers/${encodeURIComponent(appUserId)}${buildQuery(q)}`);
/** Device numbers ignore the environment (the param is only echoed); the filter param is `app_id`. */
export const getDevices = (q: { app_id?: string; period?: PeriodKey; environment: Environment }) =>
  adminGet<DeviceActivityResponse>(`/api/admin/devices${buildQuery(q)}`);
/** Usage is environment-agnostic (events carry none); the param is only echoed. The filter param is `app_id`. */
export const getUsage = (q: { app_id?: string; period?: PeriodKey; environment: Environment }) =>
  adminGet<UsageResponse>(`/api/admin/usage${buildQuery(q)}`);
/** Download URL (no fetch): the browser sends the session cookie, same-origin via the Pages Function. */
export const transactionsCsvUrl = (appId: string, s: Scope, o: { includeUser?: boolean } = {}) =>
  `/api/admin/export/transactions.csv${buildQuery({ ...s, app_id: appId, include_user: o.includeUser ? "1" : undefined })}`;
export const getSettings = () => adminGet<SettingsResponse>("/api/admin/settings");

/** Row of GET /admin/apps (secrets never included; keys are prefix + id only). */
export interface AdminAppRow {
  id: string;
  name: string;
  /** Owner override; the shown name is `display_name ?? name`. */
  display_name: string | null;
  icon_url: string | null;
  bundle_id: string | null;
  asc_app_id: string | null;
  status: "pending" | "active" | "archived";
  source: "asc" | "skill" | "webhook";
  command_center_url: string | null;
  notif_url_state: string | null;
  asc_synced_at: string | null;
  created_at: string;
  hidden: number;
  keys: Array<{ key_id: string; kind: string; prefix: string; created_at: string; revoked_at: string | null }>;
}
/** `includeHidden`: also return hidden apps (Settings needs them to be able to un-hide). */
export const getApps = (includeHidden = false) =>
  adminGet<{ apps: AdminAppRow[] }>(`/api/admin/apps${includeHidden ? "?include_hidden=1" : ""}`);

export interface AdminAppDetail {
  app: Omit<AdminAppRow, "keys"> & { icon_url: string | null; apple_commission: number; apple_app_id: number | null; notify_new_account: number };
  keys: AdminAppRow["keys"];
  products: Array<{ id: string; type: string; period: string | null; status: string }>;
}
export const getAppDetail = (id: string) => adminGet<AdminAppDetail>(`/api/admin/apps/${encodeURIComponent(id)}`);

export interface InboxItem {
  id: string;
  type: string;
  app_id: string | null;
  title: string;
  detail: string | null;
  status: string;
  created_at: string;
}
export const getInbox = () => adminGet<{ items: InboxItem[] }>("/api/admin/inbox");
export const resolveInbox = (id: string, action: "dismiss" | "adopt") =>
  adminSend<{ ok?: boolean; skipped?: string }>("POST", `/api/admin/inbox/${encodeURIComponent(id)}/resolve`, { action });
export const syncAsc = (bundleId?: string) =>
  adminSend<Record<string, unknown>>("POST", `/api/admin/asc/sync${buildQuery({ bundleId })}`);
export const patchApp = (id: string, body: Record<string, unknown>) =>
  adminSend<{ ok: true }>("PATCH", `/api/admin/apps/${encodeURIComponent(id)}`, body);
export const mintKey = (id: string, revokeExisting: boolean) =>
  adminSend<{ app_id: string; public_key: string; key_id: string }>("POST", `/api/admin/apps/${encodeURIComponent(id)}/keys`, {
    revoke_existing: revokeExisting,
  });
